import { fetchMockInventoryData, Inventory } from "../shared/mockData";
import { setBordersOnRange } from "./dashboard";

function columnToNumber(col) {
  let num = 0;
  for (let i = 0; i < col.length; i++) {
    num = num * 26 + (col.charCodeAt(i) - 64); // 'A' = 65
  }
  return num;
}

function increaseCell(cell, offset) {
  // Split into column letters and row number
  const match = cell.match(/^([A-Z]+)(\d+)$/);
  if (!match) throw new Error("Invalid cell format");

  const col = match[1];
  const row = match[2];

  const colNum = columnToNumber(col);
  const newCol = getColumnLetter(colNum + offset);

  return newCol + row;
}

function getColumnLetter(colIndex: number) {
  let letter = "";
  while (colIndex > 0) {
    let remainder = (colIndex - 1) % 26;
    letter = String.fromCharCode(65 + remainder) + letter;
    colIndex = Math.floor((colIndex - 1) / 26);
  }
  return letter;
}

async function upsertInventoryWorksheet(context: Excel.RequestContext) {
  let inventorySheet: Excel.Worksheet;
  const worksheets = context.workbook.worksheets;
  worksheets.load("items/name");
  await context.sync();

  let inventoryExists = false;
  worksheets.items.forEach((sheet) => {
    if (sheet.name === "Inventory") {
      inventoryExists = true;
      inventorySheet = sheet;
    }
  });

  if (inventoryExists) {
    inventorySheet!.delete();
    await context.sync();
  }

  inventorySheet = worksheets.add("Inventory");
  inventorySheet.activate();
  return inventorySheet;
}

async function generateInventoryDashboard() {
  const inventoryData = await fetchMockInventoryData();
  const dataHeaders = [
    "SKU",
    "Fulfillable Quantity",
    "Total Unfulfillable Quantity",
    "Total Reserved Quantity",
  ];

  const rowDataHeaders = [
    "ASIN",
    "FN SKU",
    "Seller SKU",
    "Condition",
    "Last Updated Time",
    "Product Name",
    "Total Quantity",
    "Supplier Name",
    "Lead Time",
  ];

  const dataValues = inventoryData.map((item: Inventory) => [
    item.sellerSku,
    item.fulfillableQuantity,
    item.totalUnfulfillableQuantity,
    item.totalReservedQuantity,
  ]);

  const rowDataValues = inventoryData.map((item: Inventory) => [
    item.asin,
    item.fnSku,
    item.sellerSku,
    item.condition,
    item.lastUpdatedTime,
    item.productName,
    item.totalQuantity,
    item.supplierName,
    item.leadTime,
  ]);

  const totalDataValues = inventoryData.reduce(
    (acc: number, item: Inventory) => acc + item.totalQuantity,
    0
  );
  const totalFulfillableQuantity = inventoryData.reduce(
    (acc: number, item: Inventory) => acc + item.fulfillableQuantity,
    0
  );

  const totalUnfulfillableQuantity = inventoryData.reduce(
    (acc: number, item: Inventory) => acc + item.totalUnfulfillableQuantity,
    0
  );
  const totalReservedQuantity = inventoryData.reduce(
    (acc: number, item: Inventory) => acc + item.totalReservedQuantity,
    0
  );

  await Excel.run(async (context) => {
    const inventorySheet = await upsertInventoryWorksheet(context);

    const titleRange = inventorySheet.getRange("A1:K2");
    titleRange.merge();
    inventorySheet.getRange("A1").values = [["Inventory Dashboard"]];
    titleRange.format.font.size = 16;
    titleRange.format.font.bold = true;
    titleRange.format.horizontalAlignment = "Center";
    titleRange.format.verticalAlignment = "Center";
    titleRange.format.fill.color = "#F0F4F8";
    titleRange.format.font.color = "#1F4788";

    // ============ THIRD ROW FOR FILTER INFORMATION ============
    const thirdRowRange = inventorySheet.getRange("A3:K3");
    thirdRowRange.format.fill.color = "#F0F4F8";
    
    // Get filter parameters from storage for Inventory dashboard
    const filterParams = await OfficeRuntime.storage.getItem('dash_filter');
    
    if (filterParams) {
        const params = JSON.parse(filterParams);
        
        // Display filtered SKUs centered below the dashboard heading (B3:F3)
        if (params.skus && params.skus.length > 0) {
            const skuText = params.skus.join(", ");
            const filterText = `Filtered SKUs: ${skuText}`;

            // Display filtered SKUs centered in B3:F3 below the dashboard heading
            const centeredSkuRange = inventorySheet.getRange("B3:F3");
            centeredSkuRange.merge();
            inventorySheet.getRange("B3").values = [[filterText]];
            centeredSkuRange.format.font.size = 10;
            centeredSkuRange.format.font.italic = true;
            centeredSkuRange.format.font.color = "#666666";
            centeredSkuRange.format.horizontalAlignment = "Center";
            centeredSkuRange.format.verticalAlignment = "Center";
            centeredSkuRange.format.fill.color = "#F0F4F8";
        } else {
            // Clear the cells if no SKUs are filtered
            inventorySheet.getRange("K3").values = [[""]];
            inventorySheet.getRange("A3").values = [[""]];
            try { inventorySheet.getRange("B3:F3").unmerge(); } catch (e) { /* ignore if not merged */ }
            inventorySheet.getRange("B3:F3").clear(Excel.ClearApplyTo.contents);
        }
    }

    const totalRange = inventorySheet.getRange("A4:B6");
    totalRange.merge();
    inventorySheet.getRange("A4").values = [[`Total Quantity\n${totalDataValues}`]];
    totalRange.format.font.bold = true;
    totalRange.format.horizontalAlignment = "Center";
    totalRange.format.verticalAlignment = "Center";
    setBordersOnRange(totalRange, "#28A745", Excel.BorderWeight.thick);

    const fulfillableRange = inventorySheet.getRange("C4:E6");
    fulfillableRange.merge();
    inventorySheet.getRange("C4").values = [
      [`Total Fulfillable Quantity\n${totalFulfillableQuantity}`],
    ];
    fulfillableRange.format.font.bold = true;
    fulfillableRange.format.horizontalAlignment = "Center";
    fulfillableRange.format.verticalAlignment = "Center";
    setBordersOnRange(fulfillableRange, "#2196F3", Excel.BorderWeight.thick);

    const unfulfillableRange = inventorySheet.getRange("F4:H6");
    unfulfillableRange.merge();
    inventorySheet.getRange("F4").values = [
      [`Total Unfulfillable Quantity\n${totalUnfulfillableQuantity}`],
    ];
    unfulfillableRange.format.font.bold = true;
    unfulfillableRange.format.horizontalAlignment = "Center";
    unfulfillableRange.format.verticalAlignment = "Center";
    setBordersOnRange(unfulfillableRange, "#FFC107", Excel.BorderWeight.thick);

    const reservedRange = inventorySheet.getRange("I4:K6");
    reservedRange.merge();
    inventorySheet.getRange("I4").values = [[`Total Reserved Quantity\n${totalReservedQuantity}`]];
    reservedRange.format.font.bold = true;
    reservedRange.format.horizontalAlignment = "Center";
    reservedRange.format.verticalAlignment = "Center";
    setBordersOnRange(reservedRange, "#9C27B0", Excel.BorderWeight.thick);

    let endRow = 24;
    let startRow = 8;

    // ============ CHART 1: TREEMAP - Stock Distribution by SKU (FULL WIDTH) ============
    const treemapHeaders = ["SKU", "Total Quantity"];
    const treemapData = inventoryData
      .map((item: Inventory) => [item.sellerSku, item.totalQuantity])
      .sort((a, b) => (b[1] as number) - (a[1] as number))
      .slice(0, 20); // Top 20 SKUs

    if (treemapData.length > 0) {
      const treemapHeaderRange = inventorySheet.getRange("AA1:AB1");
      treemapHeaderRange.values = [treemapHeaders];
      treemapHeaderRange.format.fill.color = "#4472C4";
      treemapHeaderRange.format.font.color = "white";
      treemapHeaderRange.format.font.bold = true;
      treemapHeaderRange.format.horizontalAlignment = "Center";

      const treemapDataRange = inventorySheet.getRange(`AA2:AB${treemapData.length + 1}`);
      treemapDataRange.values = treemapData;

      const treemapChart = inventorySheet.charts.add(
        Excel.ChartType.treemap,
        inventorySheet.getRange(`AA1:AB${treemapData.length + 1}`)
      );
      treemapChart.setPosition(`A${startRow}`, `K${endRow}`);
      treemapChart.title.text = "Stock Distribution by SKU (Top 20)";
    }

    // Move to second row for Chart 2
    startRow = endRow + 2;
    endRow = startRow + 18;

    // ============ CHART 2: COMBO CHART - Inventory Status (FULL WIDTH - ROW 2) ============
    const comboHeaders = ["SKU", "Fulfillable", "Unfulfillable", "Reserved"];
    const comboData = inventoryData
      .map((item: Inventory) => [
        item.sellerSku,
        item.fulfillableQuantity,
        item.totalUnfulfillableQuantity,
        item.totalReservedQuantity
      ])
      .sort((a, b) => (b[1] as number) - (a[1] as number))
      .slice(0, 15); // Top 15 SKUs

    if (comboData.length > 0) {
      const comboHeaderRange = inventorySheet.getRange("AD1:AG1");
      comboHeaderRange.values = [comboHeaders];
      comboHeaderRange.format.fill.color = "#4472C4";
      comboHeaderRange.format.font.color = "white";
      comboHeaderRange.format.font.bold = true;
      comboHeaderRange.format.horizontalAlignment = "Center";

      const comboDataRange = inventorySheet.getRange(`AD2:AG${comboData.length + 1}`);
      comboDataRange.values = comboData;

      const comboChart = inventorySheet.charts.add(
        Excel.ChartType.columnClustered,
        inventorySheet.getRange(`AD1:AG${comboData.length + 1}`)
      );
      
      comboChart.setPosition(`A${startRow}`, `K${endRow}`);
      comboChart.title.text = "Inventory Status Overview";
      comboChart.axes.valueAxis.title.text = "Quantity";
      comboChart.axes.categoryAxis.title.text = "SKU";

      const comboSeries = comboChart.series;
      comboSeries.getItemAt(0).name = "Fulfillable";
      comboSeries.getItemAt(1).name = "Unfulfillable";
      comboSeries.getItemAt(2).name = "Reserved";
    }

    // Move to third row for Charts 3 and 4
    startRow = endRow + 2;
    endRow = startRow + 18;

    // ============ CHART 3: STACKED COLUMN - Stock Health Analysis (LEFT SIDE - ROW 3) ============
    // Categorize stock into health buckets based on fulfillable percentage
    const stockHealthHeaders = ["Category", "Count"];
    const healthyStock = inventoryData.filter((item: Inventory) => {
      const fulfillablePct = item.totalQuantity > 0 ? (item.fulfillableQuantity / item.totalQuantity) * 100 : 0;
      return fulfillablePct >= 80;
    }).length;

    const warningStock = inventoryData.filter((item: Inventory) => {
      const fulfillablePct = item.totalQuantity > 0 ? (item.fulfillableQuantity / item.totalQuantity) * 100 : 0;
      return fulfillablePct >= 50 && fulfillablePct < 80;
    }).length;

    const criticalStock = inventoryData.filter((item: Inventory) => {
      const fulfillablePct = item.totalQuantity > 0 ? (item.fulfillableQuantity / item.totalQuantity) * 100 : 0;
      return fulfillablePct < 50;
    }).length;

    const stockHealthData = [
      ["Healthy (≥80%)", healthyStock],
      ["Warning (50-79%)", warningStock],
      ["Critical (<50%)", criticalStock]
    ];

    const healthHeaderRange = inventorySheet.getRange("AI1:AJ1");
    healthHeaderRange.values = [stockHealthHeaders];
    healthHeaderRange.format.fill.color = "#4472C4";
    healthHeaderRange.format.font.color = "white";
    healthHeaderRange.format.font.bold = true;
    healthHeaderRange.format.horizontalAlignment = "Center";

    const healthDataRange = inventorySheet.getRange("AI2:AJ4");
    healthDataRange.values = stockHealthData;

    const healthChart = inventorySheet.charts.add(
      Excel.ChartType.columnStacked,
      inventorySheet.getRange("AI1:AJ4")
    );
    healthChart.setPosition(`A${startRow}`, `E${endRow}`);
    healthChart.title.text = "Stock Health Distribution";
    healthChart.axes.valueAxis.title.text = "Number of SKUs";
    healthChart.axes.categoryAxis.title.text = "Health Status";

    // ============ CHART 4: BUBBLE CHART - Supplier Performance Matrix (RIGHT SIDE - ROW 3) ============
    const bubbleHeaders = ["Supplier", "Total Stock", "Avg Lead Time", "SKU Count"];
    
    // Aggregate by supplier
    const supplierAgg: { [key: string]: { stock: number; leadTime: number[]; skuCount: number } } = {};
    inventoryData.forEach((item: Inventory) => {
      const supplier = item.supplierName || "Unknown";
      if (!supplierAgg[supplier]) {
        supplierAgg[supplier] = { stock: 0, leadTime: [], skuCount: 0 };
      }
      supplierAgg[supplier].stock += item.totalQuantity;
      if (item.leadTime) {
        supplierAgg[supplier].leadTime.push(item.leadTime);
      }
      supplierAgg[supplier].skuCount += 1;
    });

    const bubbleData = Object.entries(supplierAgg).map(([name, data]) => {
      const avgLeadTime = data.leadTime.length > 0 
        ? data.leadTime.reduce((a, b) => a + b, 0) / data.leadTime.length 
        : 0;
      return [name, data.stock, avgLeadTime, data.skuCount];
    });

    if (bubbleData.length > 0) {
      const bubbleHeaderRange = inventorySheet.getRange("AL1:AO1");
      bubbleHeaderRange.values = [bubbleHeaders];
      bubbleHeaderRange.format.fill.color = "#4472C4";
      bubbleHeaderRange.format.font.color = "white";
      bubbleHeaderRange.format.font.bold = true;
      bubbleHeaderRange.format.horizontalAlignment = "Center";

      const bubbleDataRange = inventorySheet.getRange(`AL2:AO${bubbleData.length + 1}`);
      bubbleDataRange.values = bubbleData;

      const bubbleChart = inventorySheet.charts.add(
        Excel.ChartType.bubble,
        inventorySheet.getRange(`AL1:AO${bubbleData.length + 1}`)
      );
      bubbleChart.setPosition(`F${startRow}`, `K${endRow}`);
      bubbleChart.title.text = "Supplier Performance Matrix";
      bubbleChart.axes.valueAxis.title.text = "Total Stock Quantity";
      bubbleChart.axes.categoryAxis.title.text = "Average Lead Time (Days)";
      bubbleChart.legend.visible = true;
    }

    startRow = endRow + 2;
    endRow = startRow + 18;

    // ============ CHART 5: FUNNEL - Stock Flow Analysis ============
    const funnelHeaders = ["Stage", "Quantity"];
    const funnelData = [
      ["Total Stock", totalDataValues],
      ["Fulfillable", totalFulfillableQuantity],
      ["Reserved", totalReservedQuantity]
    ];

    const funnelHeaderRange = inventorySheet.getRange("AQ1:AR1");
    funnelHeaderRange.values = [funnelHeaders];
    funnelHeaderRange.format.fill.color = "#4472C4";
    funnelHeaderRange.format.font.color = "white";
    funnelHeaderRange.format.font.bold = true;
    funnelHeaderRange.format.horizontalAlignment = "Center";

    const funnelDataRange = inventorySheet.getRange("AQ2:AR4");
    funnelDataRange.values = funnelData;

    const funnelChart = inventorySheet.charts.add(
      Excel.ChartType.funnel,
      inventorySheet.getRange("AQ1:AR4")
    );
    funnelChart.setPosition(`A${startRow}`, `K${endRow}`);
    funnelChart.title.text = "Stock Flow: Total → Fulfillable → Reserved";

    // ============ DATA TABLE AT THE BOTTOM ============
    if (rowDataValues.length > 0) {
      const tableStartRow = endRow + 5;
      const lastCol = increaseCell("B" + tableStartRow, rowDataHeaders.length - 1);
      const headerRowDataRange = inventorySheet.getRange(`B${tableStartRow}:${lastCol}`);
      headerRowDataRange.values = [rowDataHeaders];
      headerRowDataRange.format.fill.color = "#4472C4";
      headerRowDataRange.format.font.color = "white";
      headerRowDataRange.format.font.bold = true;
      headerRowDataRange.format.horizontalAlignment = "Center";

      const match = lastCol.match(/^([A-Z]+)(\d+)$/);
      const col = match[1];
      const lastRow = Number(match[2]);

      // Create table including headers
      const tableRange = inventorySheet.getRange(
        `B${tableStartRow}:${col}${lastRow + rowDataValues.length}`
      );
      const rowDataRange = inventorySheet.getRange(
        `B${tableStartRow + 1}:${col}${lastRow + rowDataValues.length}`
      );
      rowDataRange.values = rowDataValues;
      // Set specific column widths instead of using autofit
      const tableStartCol = "B";
      const tableEndCol = col;
      inventorySheet.getRange(`${tableStartCol}:${tableEndCol}`).format.columnWidth = 15;

      // Create table with headers included
      const rowDataTable = inventorySheet.tables.add(tableRange, true);
      rowDataTable.name = "RowDataTable";

      // Disable filters on the table
      rowDataTable.showFilterButton = false;
    }

    await context.sync();
  });
}

export { generateInventoryDashboard };